import React, { useState, useEffect } from 'react';
import { Loader2, AlertTriangle, CheckCircle, X, CreditCard, Tag } from 'lucide-react';
import { api } from '../services/api';
import { useTranslation } from 'react-i18next';

interface PlanConfirmationModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: (couponCode?: string) => void;
    isLoading: boolean;
    planName: string;
    price: number;
    cycle: string;
    isUpgrade: boolean;
    taxRate: number;
}

const PlanConfirmationModal: React.FC<PlanConfirmationModalProps> = ({
    isOpen,
    onClose,
    onConfirm,
    isLoading,
    planName,
    price,
    cycle,
    isUpgrade,
    taxRate,
}) => {
    const [couponCode, setCouponCode] = useState('');
    const [promoDetails, setPromoDetails] = useState<any>(null);
    const [couponError, setCouponError] = useState('');
    const [isValidating, setIsValidating] = useState(false);
    const { t } = useTranslation('billing');

    useEffect(() => {
        if (isOpen) {
            setCouponCode('');
            setPromoDetails(null);
            setCouponError('');
        }
    }, [isOpen]);

    const handleApplyCoupon = async () => {
        if (!couponCode.trim()) return;
        setIsValidating(true);
        setCouponError('');
        setPromoDetails(null);

        try {
            const result = await api.validateCoupon(couponCode.trim());
            if (result.valid) {
                setPromoDetails(result);
            } else {
                setCouponError(result.message || 'Invalid coupon');
            }
        } catch (e) {
            setCouponError('Validation failed');
        } finally {
            setIsValidating(false);
        }
    };

    if (!isOpen) return null;

    // Calculate display price (Price is Net Base)
    let basePrice = price;
    let discountLabel = '';

    if (promoDetails) {
        if (promoDetails.discountType === 'percentage') {
            const discount = (price * promoDetails.discountValue) / 100;
            basePrice = price - discount;
            discountLabel = `${promoDetails.discountValue}% OFF`;
        } else {
            basePrice = Math.max(0, price - promoDetails.discountValue);
            discountLabel = `-€${promoDetails.discountValue}`;
        }
    }

    // Calculate VAT and Total
    const vatAmount = basePrice * taxRate;
    const totalPrice = basePrice + vatAmount;

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm !m-0">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-in fade-in zoom-in duration-200">
                <div className="p-6">
                    {/* Header */}
                    <div className="flex justify-between items-start mb-6">
                        <div className="flex items-center gap-3">
                            <div className={`p-3 rounded-xl ${isUpgrade ? 'bg-blue-50 text-blue-600' : 'bg-slate-50 text-slate-600'}`}>
                                <CreditCard size={24} />
                            </div>
                            <div>
                                <h3 className="text-lg font-bold text-slate-900">{isUpgrade ? t('modals.upgrade_plan.title_upgrade') : t('modals.upgrade_plan.title_change')}</h3>
                                <p className="text-sm text-slate-500">{t('modals.upgrade_plan.subtitle')}</p>
                            </div>
                        </div>
                        <button onClick={onClose} disabled={isLoading} className="text-slate-400 hover:text-slate-600 transition-colors">
                            <X size={24} />
                        </button>
                    </div>

                    {/* Plan Details */}
                    <div className="bg-slate-50 rounded-xl p-4 border border-slate-100 mb-6">
                        <div className="flex justify-between items-center">
                            <div>
                                <p className="font-bold text-slate-900 text-lg capitalize">{planName}</p>
                                <p className="text-xs text-slate-500 capitalize">{cycle} {t('modals.upgrade_plan.billing')}</p>
                            </div>
                            <div className="text-right">
                                {promoDetails && <p className="text-xs text-slate-400 line-through">€{price}</p>}
                                <p className="font-bold text-slate-900 text-lg">€{Math.round(totalPrice * 100) / 100}</p>
                                <p className="text-xs text-slate-500">/{cycle === 'yearly' ? t('modals.upgrade_plan.year') : t('modals.upgrade_plan.month')}</p>
                                {vatAmount > 0 ? (
                                    <p className="text-[10px] text-slate-400 mt-0.5">
                                        {t('modals.upgrade_plan.vat_incl', { base: basePrice.toFixed(2), vat: vatAmount.toFixed(2) })}
                                    </p>
                                ) : (
                                    <p className="text-[10px] text-slate-400 mt-0.5">{t('modals.upgrade_plan.vat_excl')}</p>
                                )}
                            </div>
                        </div>
                        {promoDetails && (
                            <div className="mt-2 flex items-center gap-2 text-xs font-medium text-green-600 bg-green-50 px-2 py-1 rounded w-fit">
                                <Tag size={12} />
                                <span>{t('modals.upgrade_plan.coupon_applied', { label: discountLabel })}</span>
                            </div>
                        )}
                    </div>

                    {/* Coupon Input */}
                    {!promoDetails && (
                        <div className="flex gap-2 mb-6">
                            <input
                                type="text"
                                placeholder={t('modals.upgrade_plan.coupon_placeholder')}
                                value={couponCode}
                                onChange={(e) => setCouponCode(e.target.value)}
                                className="flex-1 border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-black uppercase"
                            />
                            <button
                                onClick={handleApplyCoupon}
                                disabled={!couponCode || isValidating}
                                className="px-3 py-2 bg-slate-100 text-slate-600 rounded-lg text-sm font-medium hover:bg-slate-200 disabled:opacity-50"
                            >
                                {isValidating ? <Loader2 size={14} className="animate-spin" /> : t('modals.upgrade_plan.apply')}
                            </button>
                        </div>
                    )}
                    {couponError && <p className="text-xs text-red-500 mb-4 -mt-4">{couponError}</p>}

                    {/* Actions */}
                    <div className="space-y-3">
                        <button
                            onClick={() => onConfirm(promoDetails ? promoDetails.code : undefined)}
                            disabled={isLoading}
                            className="w-full py-3 px-4 bg-black text-white font-bold rounded-xl hover:bg-slate-800 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                        >
                            {isLoading ? <Loader2 size={18} className="animate-spin" /> : <CheckCircle size={18} />}
                            {isLoading ? t('modals.upgrade_plan.processing') : `${t('modals.upgrade_plan.proceed')} ${promoDetails ? '(€' + totalPrice.toFixed(2) + ')' : ''}`}
                        </button>
                        <button
                            onClick={onClose}
                            disabled={isLoading}
                            className="w-full py-2 text-sm text-slate-500 hover:text-slate-800 font-medium"
                        >
                            {t('modals.upgrade_plan.cancel')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PlanConfirmationModal;
